import React from 'react';
import * as S from './Reporting.styled';
import { CombinedSkuDetailsText } from 'pages/Logs/CombinedSkuDetailsText';
import { formatReportLogAmountDisplay, sanitizeAlertLevelSummary } from './reportActivityLogs';
import {
  reportLogDateTimeLabel,
  reportLogPreviewDetails,
  groupDescriptionBlocksForReportPreview,
  summarizeReportLogGroupTotal,
} from './reportLogPreview';
import type { ReportDescriptionBlock } from './reportDescriptionBlocks';
import { reportPictureDisplaySrc } from './reportDescriptionBlocks';
import type { ReportActivitySet } from './reportActivitySets';
import ReportMetricsCarousel from './ReportMetricsCarousel';
import { buildReportMetrics, defaultReportActivitySetChartsLabel } from './reportMetrics';
import ReportContextUsersPreview from './ReportContextUsersPreview';
import {
  sanitizeReportContextUsers,
  type ReportContextUser,
} from './reportContextUsers';
import { sanitizeReportTextHtml, stripReportTextHtml } from './reportTextHtml';

type Props = {
  subject?: string,
  descBlocks: ReportDescriptionBlock[],
  currency: string,
  mutedEmpty?: boolean,
  trackAnchors?: boolean,
  setPreviewAnchorRef?: (id: string, el: HTMLElement | null) => void,
  activitySets?: ReportActivitySet[],
  editingActivitySetId?: string | null,
  previewActiveSectionRef?: React.Ref<HTMLDivElement>,
  /** Compliance people — same chips as Forms / Compliance preview. */
  contextUsers?: ReportContextUser[],
  alertLevelSummary?: string,
  /** Command Center — render sanitized HTML in body text blocks. */
  richTextBody?: boolean,
};

const StaffReportComposerPreviewBody = ({
  subject,
  descBlocks,
  currency,
  mutedEmpty = false,
  trackAnchors = false,
  setPreviewAnchorRef,
  activitySets = [],
  editingActivitySetId = null,
  previewActiveSectionRef,
  contextUsers: contextUsersRaw,
  alertLevelSummary: alertLevelSummaryRaw,
  richTextBody = false,
}: Props) => {
  const subjectPlain = String(subject || '').trim();
  const contextUsers = sanitizeReportContextUsers(contextUsersRaw);
  const alertLevelSummary = sanitizeAlertLevelSummary(alertLevelSummaryRaw);
  const blockHasPreviewContent = (b: ReportDescriptionBlock) => (
    (b.kind === 'text' && Boolean(stripReportTextHtml(String(b.text || ''))))
    || b.kind === 'log'
    || b.kind === 'picture'
  );
  const hasBody = activitySets.length
    ? activitySets.some((set) => (
      (set.descBlocks || []).some(blockHasPreviewContent)
    )) || descBlocks.some(blockHasPreviewContent)
    : descBlocks.some(blockHasPreviewContent);
  const hasCompliance = contextUsers.length > 0 || Boolean(alertLevelSummary);

  if (!subjectPlain && !hasBody && !hasCompliance) {
    return (
      <S.ReportPreviewText $muted={mutedEmpty}>
        Your purpose, description and referenced activity will appear here.
      </S.ReportPreviewText>
    );
  }

  const renderPreviewDescBlocks = (
    blocks: ReportDescriptionBlock[],
    keyPrefix: string,
    valueMode: 'value' | 'units' = 'value',
    track = trackAnchors,
  ) => {
    const segments = groupDescriptionBlocksForReportPreview(blocks);
    return segments.map((segment) => {
      if (segment.kind === 'text') {
        // CC bodies store limited HTML; filed previews auto-detect the same.
        const looksLikeHtml = /<[a-z][\s\S]*>/i.test(String(segment.text || ''));
        const useHtml = richTextBody || looksLikeHtml;
        const html = useHtml ? sanitizeReportTextHtml(segment.text) : '';
        return (
          <S.ReportPreviewText
            key={`${keyPrefix}-${segment.id}`}
            ref={track && setPreviewAnchorRef
              ? (el) => setPreviewAnchorRef(segment.id, el)
              : undefined}
            {...(useHtml && html
              ? { dangerouslySetInnerHTML: { __html: html } }
              : { children: segment.text })}
          />
        );
      }
      if (segment.kind === 'picture') {
        const src = reportPictureDisplaySrc(segment);
        if (!src) return null;
        return (
          <S.ReportPreviewPicture key={`${keyPrefix}-${segment.id}`}>
            <S.ReportPreviewPictureImg src={src} alt={segment.fileName} />
            <S.ReportPreviewPictureCaption>{segment.fileName}</S.ReportPreviewPictureCaption>
          </S.ReportPreviewPicture>
        );
      }
      if (segment.kind !== 'logGroup') return null;
      const total = summarizeReportLogGroupTotal(segment.logs, valueMode, currency);
      return (
        <S.ReportPreviewLogGroup key={`${keyPrefix}-${segment.id}`}>
          {segment.logs.map((log) => {
            const when = reportLogDateTimeLabel(log);
            const display = formatReportLogAmountDisplay(
              valueMode,
              currency,
              log.value,
              log.units,
            );
            return (
              <S.ReportPreviewRef
                key={log.id}
                ref={track && setPreviewAnchorRef
                  ? (el) => setPreviewAnchorRef(log.id, el)
                  : undefined}
              >
                <S.ActivityTmpId $highlighted>
                  #
                  {log.tmpId}
                </S.ActivityTmpId>
                <S.ReportPreviewLogMain>
                  {when ? <S.ReportPreviewLogDate>{when}</S.ReportPreviewLogDate> : null}
                  <S.ActivityMsg>
                    <CombinedSkuDetailsText
                      text={reportLogPreviewDetails(log, false, valueMode)}
                    />
                  </S.ActivityMsg>
                </S.ReportPreviewLogMain>
                <S.ReportPreviewLogValue $na={display.isNa}>
                  {display.label}
                </S.ReportPreviewLogValue>
              </S.ReportPreviewRef>
            );
          })}
          {segment.logs.length > 1 ? (
            <S.ReportPreviewLogGroupTotal>
              <S.ReportPreviewLogGroupTotalTerm>{total.term}</S.ReportPreviewLogGroupTotalTerm>
              <S.ReportPreviewLogGroupTotalFigure>{total.figure}</S.ReportPreviewLogGroupTotalFigure>
            </S.ReportPreviewLogGroupTotal>
          ) : null}
        </S.ReportPreviewLogGroup>
      );
    });
  };

  return (
    <>
      {subjectPlain ? (
        <S.ReportPreviewSubject>{subjectPlain}</S.ReportPreviewSubject>
      ) : null}
      {/* Compliance chips sit under purpose — same layout as Forms / filed preview. */}
      {contextUsers.length ? (
        <ReportContextUsersPreview users={contextUsers} compact />
      ) : null}
      {alertLevelSummary ? (
        <S.ReportPreviewAlertSummary>
          <S.ReportPreviewAlertSummaryLabel>
            Alert level summary
          </S.ReportPreviewAlertSummaryLabel>
          <S.ReportPreviewAlertSummaryText>
            {alertLevelSummary}
          </S.ReportPreviewAlertSummaryText>
        </S.ReportPreviewAlertSummary>
      ) : null}
      {activitySets.map((set) => {
        const isEditing = set.id === editingActivitySetId;
        const blocksForSet = isEditing
          ? descBlocks
          : (set.descBlocks || []);
        const setValueMode = set.valueMode === 'units' ? 'units' : 'value';
        const metrics = buildReportMetrics(
          set.rows,
          'balances',
          setValueMode,
        );
        const charts = set.showCharts !== false && metrics.charts.length ? (
          <S.MetricsSetBlock>
            <ReportMetricsCarousel
              metrics={metrics}
              compact
              setLabel={(set.chartsTitle || '').trim()
                || defaultReportActivitySetChartsLabel(set.serial)}
            />
          </S.MetricsSetBlock>
        ) : null;
        return (
          <S.PreviewActivitySection
            key={set.id}
            ref={trackAnchors && isEditing ? previewActiveSectionRef : undefined}
          >
            {renderPreviewDescBlocks(
              blocksForSet,
              `set-${set.id}`,
              setValueMode,
              trackAnchors && isEditing,
            )}
            {charts}
          </S.PreviewActivitySection>
        );
      })}
      {!activitySets.length ? (
        <S.PreviewActivitySection ref={trackAnchors ? previewActiveSectionRef : undefined}>
          {renderPreviewDescBlocks(descBlocks, 'live', 'value', trackAnchors)}
        </S.PreviewActivitySection>
      ) : null}
    </>
  );
};

export default StaffReportComposerPreviewBody;
